import React, { useState, useEffect } from "react";
import api from "../api";
import DataTable from "../components/DataTable";
import CustomButton from "../components/CustomButton";
import ModalForm from "../components/ModalForm";
import CustomTextInput from "../components/CustomTextInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";

export default function ClientsPage() {
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [name, setName] = useState("");
  const [isCorporate, setIsCorporate] = useState("");
  const [ruc, setRUC] = useState("");
  const [legalName, setLegalName] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  // Obtener el ordenamiento actual de la URL
  const currentSort = searchParams.get("ordering") || "";
  const currentSearch = searchParams.get("search") || "";

  const handleSortChange = (newSort) => {
    // Actualizar la URL con el nuevo ordenamiento
    setSearchParams({ ordering: newSort, search: currentSearch });
  };

  const handleSearchChange = (newSearch) => {
    // Actualizar la URL con el nuevo valor de búsqueda
    setSearchParams({ ordering: currentSort, search: newSearch });
  };

  const columnsConfig = [
    { field: "id", headerName: "ID", sortable: true },
    {
      field: "name",
      headerName: "Cliente",
      sortable: true,
    },
    {
      field: "isCorporate",
      headerName: "Tipo de cliente",
      sortable: true,
    },
    { field: "legalName", headerName: "Razón social", sortable: true },
    {
      field: "ruc",
      headerName: "RUC",
      sortable: true,
    },
    { field: "email", headerName: "Correo electrónico", sortable: true },
    { field: "phone", headerName: "Contacto", sortable: true },
    { field: "logo", headerName: "Logo", sortable: true },
  ];

  const isCorporateOptions = [
    { value: true, label: "Corporativo" },
    { value: false, label: "Particular" },
  ];

  useEffect(() => {
    async function fetchClients() {
      try {
        setLoading(true);
        const response = await api.clients.getMulti(currentSort);

        setData(response);
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchClients();
  }, [currentSort]); // Cambia la dependencia a `currentSort`

  // Función para manejar el cambio en el input de la imagen
  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLogo(file);

      // Crear vista previa de la imagen seleccionada
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Función para manejar el click en "Detalles"
  const handleDetailsClick = (clientId) => {
    navigate(`/clients/${clientId}`); // Redirige a la página de detalles del cliente
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    const formData = new FormData();
    formData.append("name", name);
    formData.append("isCorporate", isCorporate);
    formData.append("legalName", legalName);
    formData.append("ruc", ruc);
    formData.append("email", email);
    formData.append("phone", contact);
    if (logo) {
      formData.append("logo", logo);
    }

    try {
      // Usamos FormData para enviar los datos al servidor
      await api.clients.create(formData);
      setIsModalOpen(false);
      const updatedClients = await api.clients.getMulti();
      setData(updatedClients);

      setName("");
      setIsCorporate("");
      setLegalName("");
      setRUC("");
      setEmail("");
      setContact("");
      setLogo(null);
    } catch (error) {
      if (error.message) {
        // Ajuste para errores específicos de campo según la estructura proporcionada
        const fieldErrors = error.message;
        setFieldErrors(fieldErrors);
      } else {
        setError("Error al crear el cliente. Intenta nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const [opacityLevel, setOpacityLevel] = useState(0);

  useEffect(() => {
    if (loading) {
      let intervalId = setInterval(() => {
        setOpacityLevel((prev) => Math.min(prev + 0.1, 1)); // Incrementa la opacidad hasta 1
      }, 500); // Ajusta el intervalo para controlar la velocidad
      return () => clearInterval(intervalId); // Limpia el intervalo al desmontar
    } else {
      setOpacityLevel(1); // Asegúrate de que la opacidad esté al máximo al terminar de cargar
    }
  }, [loading]);

  return (
    <Layout>
      <div
        className={`p-8 space-y-10 bg-[#F8F9FB] transition-opacity ease-in-out`}
        style={{
          opacity: opacityLevel,
          transition: "opacity 0.5s linear",
        }}
      >
        <h1 className="poppins-bold text-lg text-[#1F384C] tracking-wider">
          Clientes
        </h1>
        <div className="flex justify-between items-center">
          <p className="poppins-regular text-right text-[#595A5C] text-xs tracking-wider ">
            {data.length} clientes registrados
          </p>
          <CustomButton
            text="Agregar cliente"
            onClick={() => setIsModalOpen(true)}
            icon={true}
          />
        </div>

        <DataTable
          columnsConfig={columnsConfig}
          data={data}
          loading={loading}
          onActionClick={(row) => handleDetailsClick(row.id)}
          onSortChange={handleSortChange}
          currentSort={currentSort}
          currentSearch={currentSearch}
          onSearchChange={(e) => handleSearchChange(e.target.value)}
        />
        <ModalForm
          title="Agrega un nuevo cliente"
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleFormSubmit}
          textBtn="Agregar cliente"
        >
          {/* Muestra un error general si existe */}
          {error && <p className="text-red-600 mb-4">{error}</p>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <CustomTextInput
                label="Nombre del cliente"
                placeholder="Ingresa el nombre del cliente"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                isRequired
              />
              {/* Muestra error específico para el campo "name" */}
              {fieldErrors.name && (
                <p className="text-red-600 text-sm">{fieldErrors.name[0]}</p>
              )}
            </div>
            <div>
              <CustomTextInput
                label="Correo electrónico"
                placeholder="Ingresa el correo electrónico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                isRequired
              />
              {/* Muestra error específico para el campo "email" */}
              {fieldErrors.email && (
                <p className="text-red-600 text-sm">{fieldErrors.email[0]}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <CustomTextInput
                label="Número de contacto"
                placeholder="Escribe el número de contacto"
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                isRequired
              />
              {/* Muestra error específico para el campo "phone" */}
              {fieldErrors.phone && (
                <p className="text-red-600 text-sm">{fieldErrors.phone[0]}</p>
              )}
            </div>
            <div>
              <CustomTextInput
                label="RUC"
                placeholder="Escribe el RUC del cliente"
                type="text"
                value={ruc}
                onChange={(e) => setRUC(e.target.value)}
                isRequired
              />
              {/* Muestra error específico para el campo "ruc" */}
              {fieldErrors.ruc && (
                <p className="text-red-600 text-sm">{fieldErrors.ruc[0]}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <CustomTextInput
                label="Razón social"
                placeholder="Escribe la razón del social"
                type="text"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
              />
              {/* Muestra error específico para el campo "legalName" */}
              {fieldErrors.legalName && (
                <p className="text-red-600 text-sm">
                  {fieldErrors.legalName[0]}
                </p>
              )}
            </div>
            <div>
              <label className="block text-[#8F99A6] text-xs tracking-wider">
                Logo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="mt-1 block text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:border file:border-gray-300 file:rounded-md file:text-sm  file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100"
              />
              {logoPreview && (
                <div className="mt-2">
                  <img
                    src={logoPreview}
                    alt="Vista previa del logo"
                    className="max-w-[150px] max-h-[150px] object-contain "
                  />
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <CustomTextInput
                label="Tipo de cliente"
                placeholder="Selecciona el tipo de cliente"
                type="select"
                value={isCorporate}
                onChange={(e) => setIsCorporate(e.target.value)}
                options={isCorporateOptions}
              />
              {fieldErrors.isCorporate && (
                <p className="text-red-600 text-sm">
                  {fieldErrors.isCorporate[0]}
                </p>
              )}
            </div>
          </div>
        </ModalForm>
      </div>
    </Layout>
  );
}
